<script setup>
    // Vue
    import { defineExpose, defineEmits, inject, ref, getCurrentInstance } from 'vue';

    // Store
    import { useManagementListStore } from '@/stores/management/managementListStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utils
    import Utils from '@/commonProject/helpers/utils.js';

    // Else
    import i18n from '@/common/i18n';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 利用者の管理ストア
    const managementListStore = useManagementListStore();
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // 利用者一覧検索済み項目
    const managementListRequestDone = ref({});
    // 企業契約PK情報
    const cmpContPks = ref([]);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 初期処理 親画面から利用可能関数
     */
    async function setInfo(request) {
        // 利用者一覧検索項目を取得
        managementListRequestDone.value = request;

        // Storeの企業契約情報を初期化する。
        managementListStore.contractList = [];

        // チェックボックスで選択した契約プランの一覧の配列を初期化する。
        cmpContPks.value = [];

        // 企業契約情報取得
        await getCintractList();

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalUserPlanControl .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }

    defineExpose({
        setInfo,
    });

    /**
     * 企業契約情報取得処理
     */
    async function getCintractList() {
        // 企業契約情報取得APIのクエリパラメーターを生成する。
        // クエリパラメーターは空でコールする。
        const queryParam = {};

        // 企業契約情報取得APIをコールする。
        const response = await managementListStore.getCmpConAction(API_URL, queryParam);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 利用者契約一括割当処理
     * @param OpeMode 操作種別("1"：割り当て、"2"：割り当て解除)
     */
    async function updateContract(OpeMode) {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // 利用者契約一括割当APIのリクエストボディを生成する。
        const requestBody = {
            Cond: managementListRequestDone.value,
            OpeMode: OpeMode,
            CmpContPks: cmpContPks.value,
        };

        // 利用者契約一括割当APIをコールする。
        const response = await managementListStore.postCliConBlkAction(API_URL, requestBody);

        if (response.success) {
            closeAndUpdate();
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 契約プラン
        if (cmpContPks.value.length === 0) {
            message += i18n.global.t('ManagementContract.message.message1') + '\n';
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * 親画面で実装必須関数
     */
    const emit = defineEmits(['contractModal:cancel', 'contractModal:closeAndUpdate']);

    /**
     * モーダルを閉じる。
     */
    function cancel() {
        // 親画面のキャンセル処理を呼び出す
        emit('contractModal:cancel');
    }
    /**
     * モーダルを閉じて親画面を更新する。
     */
    function closeAndUpdate() {
        // 親画面のモーダルを閉じて親画面の画面再描画処理を呼び出す。
        emit('contractModal:closeAndUpdate');
    }
</script>
<template>
    <article id="modalUserPlanControl" class="modal" :style="{ display: 'block' }">
        <h3 class="textCenter">{{ $t('ManagementContract.parts.pageTitle1') }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <p class="pageInfo" style="margin-bottom: 15px">{{ $t('ManagementContract.parts.pageInfo1') }}</p>
            <article class="innerTable" style="height: auto">
                <table class="table">
                    <tbody>
                        <tr v-for="(data, index) in managementListStore.contractList" :key="index">
                            <td style="width: 20px">
                                <input type="checkbox" :id="'data5-' + (index + 1)" class="checkbox" v-model="cmpContPks" :value="data.CmpContPk" :disabled="data.TouDataFlag" />
                                <label class="checkbox" :for="'data5-' + (index + 1)" />
                            </td>
                            <td style="width: auto">
                                <span>{{ index + 1 }}. {{ data.CmpContNm }} ({{ data.CmpContPeriodS }} - {{ data.CmpContPeriodE }})</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </article>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <!--キャンセル-->
                    <button type="button" class="btnNormal closeModal" @click="cancel()">{{ $t('ManagementContract.parts.button1') }}</button>
                </li>
                <li>
                    <!--割り当てる-->
                    <button type="button" class="btnPrimary closeModal" @click="updateContract('1')" :disabled="disabledStore.isDisabled">{{ $t('ManagementContract.parts.button2') }}</button>
                </li>
                <li>
                    <!--割り当てを解除する-->
                    <button type="button" class="btnPrimary closeModal" @click="updateContract('2')" :disabled="disabledStore.isDisabled">{{ $t('ManagementContract.parts.button3') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>
<style></style>
